import * as React from 'react';
import { Button, Col, Row } from 'antd';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { resolve } from 'inversify-react';
import { Link } from 'react-router-dom';

import { FormatService } from 'src/core/services/format.service';
import { TextView } from 'src/core/ui/text.view';
import { DataTableView, ToolbarVisibility, Column } from 'src/core/ui/data-table.view';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { DateView } from 'src/core/ui/date.view';
import { NoteView } from '../shared/note.view';
import { ProductTypeEditorController } from 'src/app/products/products/parts/product-type-editor.controller';
import { ProductServiceTypeEditorController } from 'src/app/products/product-service-types/parts/product-service-type-editor.controller';
import { ProductCategoryEditorController } from 'src/app/products/product-categories/parts/product-category-editor.controller';
import { SupplierEditorController } from 'src/app/products/suppliers/parts/supplier-editor.controller';
import { ClientItemEditorController } from 'src/app/crm/clients/parts/client-item-editor.controller';
import { UserEditorController } from 'src/app/general/users/parts/user-editor.controller';
import { ChangeStatusModalView } from 'src/app/bookings/quote-entries/parts/change-status-modal.view';
import { IdentityService } from 'src/core/services/identity.service';
import { CollapseCard } from 'src/app/shared/collapse-card.view';
import { StatusTexts } from "src/app/shared/status-texts";

interface ConfirmationQuoteProps extends ComponentViewProps<DataSet<ConfirmationQuoteItem>> {
	filterQuoteEntry: (filter: ConfirmationQuoteFilter) => Promise<void>;
	historyDataService: DataSet<QuoteEntryStateHistory>;
	loadStateHistory: (entry: QuoteEntryItem) => void;
	changeStatus: (entry: QuoteEntryItem, observations: string, confirmationNumber: string, rejectedAs?: number) => Promise<void>;
}

@observer
export class ConfirmationQuoteView extends React.Component<ConfirmationQuoteProps> {

	@resolve
	private identity: IdentityService;

	@resolve
	formatService: FormatService;

	@observable
	private showStateDialog: ConfirmationQuoteItem;

	private columns: Column[] = [
		{
			key: 'command',
			title: '',
			render: (text: any, record: ConfirmationQuoteItem, index: number) => {
				let status;
				let style: any = {
				};
				switch (record.entry.status) {
					case 1:
					case 2:
						status = 'primary';
						style.backgroundColor = '#ffffcd';
						style.borderColor = 'black';
						style.color = 'black';
						break;
					case 3:
						status = 'primary';
						style.backgroundColor = '#28a745';
						style.borderColor = '#28a745';
						break;
					case 4:
					case 5:
						status = 'danger';
						break;
				}
				return (
					<Button.Group>

						<NoteView
							identityService={this.identity}
							mode="display"
							noteProperty="notes"
							record={record.entry.dynamicProperties}
							setBusy={this.props.setBusy}
						/>

						<Button
							disabled={this.props.model.isDirty}
							type={status}
							style={style}
							onClick={this.loadStateHistory(record)}
						>
							{this.entryStatusText(record.entry)}
						</Button>
					</Button.Group>
				);
			}
		},
		{
			key: 'quoteNumber',
			dataIndex: 'quoteNumber',
			title: 'Quote Number',
			render: (text, record: ConfirmationQuoteItem) => <Link to={`/bookings/quotes/${record.entry.quoteId}`}>{text}</Link>,
		},
		{
			key: 'entry.product',
			dataIndex: 'entry.productDisplayName',
			title: 'Product',
			className: 'product-name',
			render: (text: any, record: ConfirmationQuoteItem, index: number) => {
				return record.entry.productDisplayName;
			}
		},
		{
			key: 'entry.supplier',
			dataIndex: 'entry.supplierTitle',
			title: 'Supplier',
			render: (text, record: ConfirmationQuoteItem, index: number) => {
				return record.entry.supplierTitle
			}
		},
		{
			key: 'entry.productTypeText',
			dataIndex: 'entry.productTypeText',
			title: 'Type',
			render: (text, record: ConfirmationQuoteItem, index: number) => {
				return this.getProductType(record.entry.productType)
			}
		},
		{
			key: 'dates',
			title: 'Date',
			children: [
				{
					key: 'entry.dateFrom',
					dataIndex: 'entry.dateFrom',
					title: 'From',
					render: (text, record: ConfirmationQuoteItem, index: number) => {
						return this.formatService.toString('small-date-time', record.entry.dateFrom, "utc");
					}
				},
				{
					key: 'entry.dateTo',
					dataIndex: 'entry.dateTo',
					title: 'To',
					render: (text, record: ConfirmationQuoteItem, index: number) => {
						return this.formatService.toString('small-date-time', record.entry.dateTo, "utc");
					}
				}
			]
		},

	];

	@observable
	private filter: ConfirmationQuoteFilter;

	constructor(props: Readonly<ConfirmationQuoteProps>) {
		super(props);
		this.filter = {
			quoteNumber: null,
			bookingDateFrom: null,
			bookingDateTo: null,
			serviceDateFrom: null,
			serviceDateTo: null,
			status: null,
			productCategoryId: null,
			productServiceTypeId: null,
			productServiceTypeCode: null,
			supplierId: null,
			clientId: null,
			salesManId: null,
			productTypeText: null,
			productType: null,
		}
	}

	private closeStateDialog = (ev?: React.MouseEvent<HTMLButtonElement>) => {
		if (ev != null) {
			ev.preventDefault();
		}
		this.showStateDialog = null;
	}

	private loadStateHistory = (item: ConfirmationQuoteItem) => () => {
		this.props.loadStateHistory(item.entry);
		this.showStateDialog = item;
	}

	private renderQuoteEntryStatusModal = () => {
		if (this.showStateDialog == null) return null;
		return (
			<ChangeStatusModalView
				quoteStatus={this.showStateDialog.quoteStatus}
				setBusy={this.props.setBusy}
				mode="edit"
				closeStateDialog={this.closeStateDialog}
				showStateDialog={this.showStateDialog.entry}
				changeStatus={this.props.changeStatus}
				historyDataService={this.props.historyDataService}
				formatService={this.formatService}
			/>
		);
	};

	private entryStatusText = (item: QuoteEntryItem) => {
		return StatusTexts.getQuoteEntryStatusFromNumber(item.status)
	}

	private getProductType(type): 'Accommodation' | 'CarRent' | 'General' | 'Transfer' | 'Flights' | 'Room' {
		switch (type) {
			case 0:
				return 'General';
			case 1:
				return 'Accommodation';
			case 2:
				return 'CarRent';
			case 3:
				return 'Transfer';
			case 4:
				return 'Flights';
			case 5:
				return 'Room';
			default:
				return 'General';
		}
	}

	public render() {
		const optionsStatus = [
			{ value: 1, label: 'Pending' },
			{ value: 2, label: 'Processing' },
			{ value: 3, label: 'Confirmed' },
			{ value: 4, label: 'CancelledByClient' },
			{ value: 5, label: 'DeniedBySupplier' },
			{ value: 6, label: 'CancelledBySupplier' }
		];
		const optionsType = [
			{ value: 0, label: 'General' },
			{ value: 1, label: 'Accomodation' },
			{ value: 2, label: 'Car Rent' },
			{ value: 3, label: 'Transfer' },
			{ value: 4, label: 'Flight' },
			{ value: 5, label: 'Room' }
		];
		return (
			<div className="window window-normal">
				<div className="panel">
					<CollapseCard
						title="Confirmation Quotes"
					>
						<div className="m-3">
							{this.renderQuoteEntryStatusModal()}
							<Row>
								<Col xs={24} md={4}>Component Type</Col>
								<Col xs={24} md={10}>
									<ProductTypeEditorController
										label=''
										mode={'edit'}
										onChanged={(value) => {
											this.filter.productTypeText = this.getProductType(value);
											this.filter.productType = value;
											if (value == null) {
												this.filter.productCategoryId = null;
												this.filter.productServiceTypeId = null;
												this.filter.productServiceTypeCode = null;
											}
										}}
										value={this.filter.productType}
										setBusy={this.props.setBusy}
										options={optionsType}
									/>
								</Col>
							</Row>
							<Row>
								<Col xs={24} md={4}>Category</Col>
								<Col xs={24} md={10}>
									<ProductCategoryEditorController
										label=''
										mode={this.filter.productType != null ? 'edit' : 'display'}
										onChanged={(value) => {
											this.filter.productCategoryId = value != null ? value.id : null;
											if (value == null) {
												this.filter.productServiceTypeId = null;
												this.filter.productServiceTypeCode = null;
											}
										}}
										value={this.filter.productCategoryId}
										productType={this.filter.productType == 1 ? [1, 5] : this.filter.productType}
										setBusy={this.props.setBusy}
									/>
								</Col>
							</Row>
							<Row>
								<Col xs={24} md={4}>Service</Col>
								<Col xs={24} md={10}>
									<ProductServiceTypeEditorController
										label=''
										categoryId={this.filter.productCategoryId}
										mode={this.filter.productCategoryId ? 'edit' : 'display'}
										onChanged={(value) => {
											this.filter.productServiceTypeId = value != null ? value.id : null;
											this.filter.productServiceTypeCode = value != null ? value.displayName : null;
										}}
										value={this.filter.productServiceTypeId}
										setBusy={this.props.setBusy}
									/>
								</Col>
							</Row>
							<Row>
								<Col xs={24} md={4}>Reservation Dates</Col>
								<Col xs={12} md={4}>
									<DateView
										label=""
										mode="edit"
										value={this.filter.bookingDateFrom}
										onChanged={(newVal) => {
											this.filter.bookingDateFrom = newVal;
										}}
										format="small-date"
										dateUsage="utc"
									/>
								</Col>
								<Col xs={12} md={{ offset: 2, span: 4 }}>
									<DateView
										label=""
										mode="edit"
										value={this.filter.bookingDateTo}
										onChanged={(newVal) => {
											this.filter.bookingDateTo = newVal;
										}}
										format="small-date"
										dateUsage="utc"
									/>
								</Col>
							</Row>
							<Row>
								<Col xs={24} md={4}>Services Dates</Col>
								<Col xs={12} md={4}>
									<DateView
										label=""
										mode="edit"
										value={this.filter.serviceDateFrom}
										onChanged={(newVal) => {
											this.filter.serviceDateFrom = newVal;
										}}
										format="small-date"
										dateUsage="utc"
									/>
								</Col>
								<Col xs={12} md={{ offset: 2, span: 4 }}>
									<DateView
										label=""
										mode="edit"
										value={this.filter.serviceDateTo}
										onChanged={(newVal) => {
											this.filter.serviceDateTo = newVal;
										}}
										format="small-date"
										dateUsage="utc"
									/>
								</Col>
							</Row>
							<Row>
								<Col xs={24} md={4}>Status</Col>
								<Col xs={24} md={10}>
									<DropdownView
										label=''
										model={this.filter.status}
										onChanged={(item) => { this.filter.status = item != null ? item.value : null; }}
										options={optionsStatus}
										placeholder="Select a Status..."
									/>
								</Col>
							</Row>
							<Row>
								<Col xs={24} md={4}>Supplier</Col>
								<Col xs={24} md={10}>
									<SupplierEditorController
										label=""
										mode='edit'
										supplierType={this.filter.productType == 2 ? 1 : 0}
										onChange={(value: SupplierSummary) => {
											this.filter.supplierId = value ? value.id : null;
										}}
										value={this.filter.supplierId}
										setBusy={this.props.setBusy}
									/>
								</Col>
							</Row>
							<Row>
								<Col xs={24} md={4}>Client</Col>
								<Col xs={24} md={10}>
									<ClientItemEditorController
										label=""
										setBusy={this.props.setBusy}
										mode='edit'
										value={this.filter.clientId}
										onChanged={newVal => {
											this.filter.clientId = newVal ? newVal.id : null;
										}}
									/>
								</Col>
							</Row>
							<Row>
								<Col xs={24} md={4}>Salesman</Col>
								<Col xs={24} md={10}>
									<UserEditorController
										label=""
										setBusy={this.props.setBusy}
										mode='edit'
										isRole={false}
										value={this.filter.salesManId}
										onChanged={(newValue) => this.filter.salesManId = newValue ? newValue.id : undefined}
									/>
								</Col>
							</Row>
							<Row>
								<Col xs={24} md={4}>Quote Number</Col>
								<Col xs={24} md={10}>
									<TextView
										label=""
										mode='edit'
										value={this.filter.quoteNumber}
										onChanged={newValue => {
											this.filter.quoteNumber = newValue == '' ? null : newValue
										}}
									/>
								</Col>
							</Row>
							<Row>
								<Button
									type='primary'
									onClick={() => { this.props.filterQuoteEntry(this.filter) }}
								>
									Search
								</Button>
							</Row>
						</div>
					</CollapseCard>
					{this.props.model != null && <DataTableView
						columns={this.columns}
						mode='edit'
						setBusy={this.props.setBusy}
						model={this.props.model}
						rowKey={(record: ConfirmationQuoteItem) => record.entry.id}
						hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideModifyButtons}

					/>}
				</div>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/confirmation-quotes/confirmation-quote.view.tsx